import { type User, useCurrentUserId } from "@entities/user"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
	removeFriendFromCache,
	updateFriendInCache,
} from "../lib/update-friend-cache"
import type { Friend } from "../model/types"
import {
	archiveFriend,
	blockFriend,
	declineMatch,
	unarchiveFriend,
	unblockFriend,
} from "./client"
import { friendsQueryOptions } from "./queries"

interface FriendActionParams {
	userId: User["id"]
	friendId: Friend["id"]
}

export function useBlockFriend() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, friendId }: FriendActionParams) =>
			blockFriend(userId, friendId),
		onMutate: async ({ userId, friendId }) => {
			const { queryKey } = friendsQueryOptions(userId)
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData(queryKey)
			updateFriendInCache(queryClient, userId, friendId, (friend) => ({
				...friend,
				status: "blocked",
			}))
			return { previous }
		},
		onError: (_err, { userId }, context) => {
			if (context?.previous) {
				const { queryKey } = friendsQueryOptions(userId)
				queryClient.setQueryData(queryKey, context.previous)
			}
		},
		onSettled: (_data, _err, { userId }) => {
			const { queryKey } = friendsQueryOptions(userId)
			queryClient.invalidateQueries({ queryKey })
		},
	})
}

export function useArchiveFriend() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, friendId }: FriendActionParams) =>
			archiveFriend(userId, friendId),
		onMutate: async ({ userId, friendId }) => {
			const { queryKey } = friendsQueryOptions(userId)
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData(queryKey)
			updateFriendInCache(queryClient, userId, friendId, (friend) => ({
				...friend,
				is_archived: true,
			}))
			return { previous }
		},
		onError: (_err, { userId }, context) => {
			if (context?.previous) {
				const { queryKey } = friendsQueryOptions(userId)
				queryClient.setQueryData(queryKey, context.previous)
			}
		},
		onSettled: (_data, _err, { userId }) => {
			const { queryKey } = friendsQueryOptions(userId)
			queryClient.invalidateQueries({ queryKey })
		},
	})
}

export function useUnarchiveFriend() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, friendId }: FriendActionParams) =>
			unarchiveFriend(userId, friendId),
		onMutate: async ({ userId, friendId }) => {
			const { queryKey } = friendsQueryOptions(userId)
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData(queryKey)
			updateFriendInCache(queryClient, userId, friendId, (friend) => ({
				...friend,
				is_archived: false,
			}))
			return { previous }
		},
		onError: (_err, { userId }, context) => {
			if (context?.previous) {
				const { queryKey } = friendsQueryOptions(userId)
				queryClient.setQueryData(queryKey, context.previous)
			}
		},
		onSettled: (_data, _err, { userId }) => {
			const { queryKey } = friendsQueryOptions(userId)
			queryClient.invalidateQueries({ queryKey })
		},
	})
}

export function useUnblockFriend() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, friendId }: FriendActionParams) =>
			unblockFriend(userId, friendId),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: friendsQueryOptions(variables.userId).queryKey,
			})
		},
	})
}

/**
 * This is the only decline command of the contract. The first call (user A,
 * «Отклонить») only records the user who declined, and user B still sees the
 * card. The call from user B («Окей» in the popup «мэтч уже неактуален») closes
 * the match. The backend gets the result from the fields of the record, not
 * from the parameters. Thus the call is idempotent.
 *
 * The hook removes the card only after the 200 response, because the match is
 * active until this response. An optimistic removal can show the card again
 * when the call fails. The hook does not catch the error, and the UI shows it.
 */
export function useDeclineFriend() {
	const userId = useCurrentUserId()
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationFn: (friend: Friend) => declineMatch(userId, friend.id),
		onSuccess: (_data, friend) => {
			removeFriendFromCache(queryClient, userId, friend.id)
		},
	})

	return {
		declineFriend: mutation.mutateAsync,
		isPending: mutation.isPending,
	}
}
